/**
 * FirmaBelgeKurallariKarti.jsx — the company document rules by the COMPANY's nationality (backlog #430, 2026-10-17; backend V99).
 *
 * A rule is (a fair, or "every fair"; a document; the company's nationality: any / Yerli / Yabancı; a status: required / optional / not applicable / not offered).
 * Two places mount it: Fuar Kuralları › Firma Belgeleri (`fuarId` = the selected fair) and Global Kurallar › Firma Belgeleri (`fuarId` 0 = every fair).
 *
 * How a company's document is judged (backend BelgeUyrukKuralService), per document: this fair's rule for the company's own nationality, then the global rule for it,
 * then this fair's "any" rule, then the global "any" rule; a document no rule names is what the catalogue says. The foreign company's old code lists are seeded as global
 * rows (Vergi Levhası and Faaliyet Belgesi not applicable; the İSG appointments and the İşveren alternative not offered). A fair that adds one document keeps the global ones.
 *
 * ⚠️ A rule changes what companies must upload and whether their card is for 24 hours only: the card says so before the change is made, and the table of the global level
 * is shown read-only under a fair's own, so the reader sees what the fair adds to.
 *
 * @param {number}  fuarId     the fair whose rules these are; 0 = every fair (the global level)
 * @param {boolean} [yazabilir] may the viewer change rules (default true: the page itself is staff-only, page.fuar.yonetim)
 */
import React, { useCallback, useEffect, useState } from "react";
import { Alert, Badge, Button, Card, CardBody, CardHeader, Input, Label, Spinner, Table } from "reactstrap";
import { Plus, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { currentLocaleTag } from "../../i18n";
import { getRequiredFirmaBelgeTypes, addRequiredFirmaBelgeType, removeRequiredFirmaBelgeType } from "../../services/cardAccessService";
import { getBelgeGereksinimleri } from "../../services/portalService";

/** The nationality levels of a company rule (the backend's UyrukConstant). */
export const FIRMA_UYRUK = { HEPSI: 0, YERLI: 1, YABANCI: 2 };

/** The statuses of a company document rule (UyrukConstant.Durum codes) and the colour of their badge. */
export const FIRMA_BELGE_DURUM = {
  1: { anahtar: "zorunlu", renk: "success" },
  2: { anahtar: "istegeBagli", renk: "info" },
  3: { anahtar: "uygulanamaz", renk: "secondary" },
  4: { anahtar: "sunulmaz", renk: "dark" },
};

const UYRUK_ANAHTAR = { 0: "hepsi", 1: "yerli", 2: "yabanci" };

function tarih(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? String(iso) : d.toLocaleDateString(currentLocaleTag());
}

function Satirlar({ satirlar, t, onSil, silinenId }) {
  return (
    <div className="table-responsive">
      <Table size="sm" className="align-middle mb-0">
        <thead className="table-light">
          <tr>
            <th>{t("firmaBelgeKurallari.belge")}</th>
            <th>{t("firmaBelgeKurallari.firmaUyrugu")}</th>
            <th>{t("firmaBelgeKurallari.durum")}</th>
            <th>{t("firmaBelgeKurallari.son")}</th>
            {onSil && <th />}
          </tr>
        </thead>
        <tbody>
          {satirlar.length === 0 && (
            <tr><td colSpan={onSil ? 5 : 4} className="text-center text-muted py-3" data-testid="firma-kural-bos">{t("firmaBelgeKurallari.kuralYok")}</td></tr>
          )}
          {satirlar.map((s) => {
            const d = FIRMA_BELGE_DURUM[s.durum] ?? FIRMA_BELGE_DURUM[1];
            return (
              <tr key={`${s.fuarId}-${s.fileTypeId}-${s.uyruk}`} data-testid="firma-kural-satir">
                <td className="small">{s.fileTypeName || `#${s.fileTypeId}`}<span className="text-muted ms-1">#{s.fileTypeId}</span></td>
                <td><Badge color="light" className="text-body border fw-normal">{t(`firmaBelgeKurallari.uyruk.${UYRUK_ANAHTAR[s.uyruk] ?? "hepsi"}`)}</Badge></td>
                <td><Badge color={d.renk} className="fw-normal">{t(`firmaBelgeKurallari.durumlar.${d.anahtar}`)}</Badge></td>
                <td className="small text-muted">{tarih(s.updateDate) ?? "—"}</td>
                {onSil && (
                  <td className="text-end">
                    <Button color="light" size="sm" disabled={silinenId === `${s.fileTypeId}-${s.uyruk}`}
                            title={t("firmaBelgeKurallari.kuraliKaldir")} aria-label={t("firmaBelgeKurallari.kuraliKaldir")}
                            data-testid="firma-kural-sil" onClick={() => onSil(s)}>
                      <Trash2 size={14} />
                    </Button>
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </Table>
    </div>
  );
}

export default function FirmaBelgeKurallariKarti({ fuarId, yazabilir = true }) {
  const { t } = useTranslation("yonetim");
  const genel = Number(fuarId) === 0;
  const [satirlar, setSatirlar] = useState(null);
  const [genelSatirlar, setGenelSatirlar] = useState([]);
  const [belgeler, setBelgeler] = useState([]);
  const [hata, setHata] = useState(null);
  const [mesaj, setMesaj] = useState(null);
  const [calisiyor, setCalisiyor] = useState(false);
  const [silinenId, setSilinenId] = useState(null);
  const [yenile, setYenile] = useState(0);
  const [form, setForm] = useState({ fileTypeId: "", uyruk: String(FIRMA_UYRUK.HEPSI), durum: "1" });

  // KURAL: fetch Effect içindeki async fonksiyonda (react-hooks/set-state-in-effect).
  useEffect(() => {
    let ignore = false;
    async function run() {
      setHata(null);
      try {
        const [bu, ust, matris] = await Promise.all([
          getRequiredFirmaBelgeTypes(fuarId),
          genel ? Promise.resolve([]) : getRequiredFirmaBelgeTypes(0),
          // the documents a rule may name: the company documents of the catalogue (the same list for every fair)
          getBelgeGereksinimleri(0).catch(() => null),
        ]);
        if (ignore) return;
        setSatirlar(bu);
        setGenelSatirlar(ust);
        const ilk = matris?.arayuzler?.[0]?.firmaBelgeleri ?? [];
        setBelgeler(ilk.map((b) => ({ fileTypeId: b.fileTypeId, ad: b.ad, grupAdi: b.grupAdi })));
      } catch (e) {
        if (!ignore) { setHata(e.message); setSatirlar([]); }
      }
    }
    run();
    return () => { ignore = true; };
  }, [fuarId, genel, yenile]);

  const degistir = useCallback((yama) => setForm((f) => ({ ...f, ...yama })), []);

  async function ekle() {
    if (!form.fileTypeId) { setHata(t("firmaBelgeKurallari.belgeSecin")); return; }
    setCalisiyor(true);
    setHata(null);
    setMesaj(null);
    try {
      await addRequiredFirmaBelgeType(Number(fuarId), Number(form.fileTypeId), { uyruk: Number(form.uyruk), durum: Number(form.durum) });
      setMesaj(t("firmaBelgeKurallari.kuralKaydedildi"));
      setForm((f) => ({ ...f, fileTypeId: "" }));
      setYenile((n) => n + 1);
    } catch (e) {
      setHata(e.message);
    } finally {
      setCalisiyor(false);
    }
  }

  async function sil(s) {
    setSilinenId(`${s.fileTypeId}-${s.uyruk}`);
    setHata(null);
    setMesaj(null);
    try {
      await removeRequiredFirmaBelgeType(Number(fuarId), s.fileTypeId, s.uyruk);
      setMesaj(t("firmaBelgeKurallari.kuralKaldirildi"));
      setYenile((n) => n + 1);
    } catch (e) {
      setHata(e.message);
    } finally {
      setSilinenId(null);
    }
  }

  return (
    <Card data-testid="firma-belge-kurallari">
      <CardHeader className="fw-semibold">
        {genel ? t("firmaBelgeKurallari.baslikGenel") : t("firmaBelgeKurallari.baslikFuar", { fuarId })}
      </CardHeader>
      <CardBody>
        <p className="small text-muted">{genel ? t("firmaBelgeKurallari.aciklamaGenel") : t("firmaBelgeKurallari.aciklamaFuar")}</p>
        <Alert color="warning" className="small py-2">{t("firmaBelgeKurallari.sirayiAnlat")}</Alert>
        {hata && <Alert color="danger" className="small py-2" data-testid="firma-kural-hata">{hata}</Alert>}
        {mesaj && <Alert color="success" className="small py-2">{mesaj}</Alert>}

        {satirlar == null ? (
          <div className="text-center py-3"><Spinner size="sm" color="primary" /></div>
        ) : (
          <Satirlar satirlar={satirlar} t={t} onSil={yazabilir ? sil : null} silinenId={silinenId} />
        )}

        {yazabilir && (
          <div className="border rounded p-2 mt-3" data-testid="firma-kural-formu">
            <div className="small fw-semibold mb-2">{t("firmaBelgeKurallari.kuralEkleDegistir")}</div>
            <div className="d-flex flex-wrap gap-2 align-items-end">
              <div style={{ minWidth: 220 }}>
                <Label className="small text-muted mb-1">{t("firmaBelgeKurallari.belge")}</Label>
                <Input type="select" bsSize="sm" value={form.fileTypeId} onChange={(e) => degistir({ fileTypeId: e.target.value })} data-testid="firma-kural-belge">
                  <option value="">{t("firmaBelgeKurallari.belgeSecin")}</option>
                  {belgeler.map((b) => <option key={`${b.grupAdi}-${b.fileTypeId}`} value={b.fileTypeId}>{b.ad} (#{b.fileTypeId})</option>)}
                </Input>
              </div>
              <div>
                <Label className="small text-muted mb-1">{t("firmaBelgeKurallari.firmaUyrugu")}</Label>
                <Input type="select" bsSize="sm" value={form.uyruk} onChange={(e) => degistir({ uyruk: e.target.value })} data-testid="firma-kural-uyruk">
                  {Object.values(FIRMA_UYRUK).map((u) => <option key={u} value={u}>{t(`firmaBelgeKurallari.uyruk.${UYRUK_ANAHTAR[u]}`)}</option>)}
                </Input>
              </div>
              <div>
                <Label className="small text-muted mb-1">{t("firmaBelgeKurallari.durum")}</Label>
                <Input type="select" bsSize="sm" value={form.durum} onChange={(e) => degistir({ durum: e.target.value })} data-testid="firma-kural-durum">
                  {Object.entries(FIRMA_BELGE_DURUM).map(([kod, d]) => <option key={kod} value={kod}>{t(`firmaBelgeKurallari.durumlar.${d.anahtar}`)}</option>)}
                </Input>
              </div>
              <Button color="primary" size="sm" disabled={calisiyor} onClick={ekle} data-testid="firma-kural-ekle">
                {calisiyor ? <Spinner size="sm" className="me-1" /> : <Plus size={14} className="me-1" />}{t("firmaBelgeKurallari.kaydet")}
              </Button>
            </div>
            <div className="small text-muted mt-2">{t(`firmaBelgeKurallari.durumAciklama.${FIRMA_BELGE_DURUM[Number(form.durum)]?.anahtar ?? "zorunlu"}`)}</div>
          </div>
        )}

        {!genel && (
          <div className="mt-4" data-testid="firma-kural-genel">
            <div className="small fw-semibold mb-1">{t("firmaBelgeKurallari.genelKurallarBaslik")}</div>
            <div className="small text-muted mb-2">{t("firmaBelgeKurallari.genelKurallarAciklama")}</div>
            <Satirlar satirlar={genelSatirlar} t={t} onSil={null} silinenId={null} />
          </div>
        )}
      </CardBody>
    </Card>
  );
}
